import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const SESSION_TIMEOUT = 720 * 60 * 1000; // 12時間
const SESSION_KEY = 'sessionExpiry';
const USER_KEY = 'userData';
const CHECK_INTERVAL = 5 * 60 * 1000; // 5分ごとにチェック
const TOKEN_KEY = 'token';

const isLocalStorageAvailable = () => {
  try {
    return typeof localStorage !== 'undefined' && localStorage !== null;
  } catch {
    return false;
  }
};

const safeLocalStorageGet = (key) => {
  try {
    if (isLocalStorageAvailable()) {
      return localStorage.getItem(key);
    }
    return null;
  } catch (error) {
    console.error('localStorage get error:', error);
    return null;
  }
};

const safeLocalStorageSet = (key, value) => {
  try {
    if (isLocalStorageAvailable()) {
      localStorage.setItem(key, value);
      return true;
    }
    return false;
  } catch (error) {
    console.error('localStorage set error:', error);
    return false;
  }
};

const safeLocalStorageRemove = (key) => {
  try {
    if (isLocalStorageAvailable()) {
      localStorage.removeItem(key);
      return true;
    }
    return false;
  } catch (error) {
    console.error('localStorage remove error:', error);
    return false;
  }
};

export function useSession() {
  const navigate = useNavigate();
  const location = useLocation();

  const getInitialAuthState = () => {
    try {
      const expiry = safeLocalStorageGet(SESSION_KEY);
      const userData = safeLocalStorageGet(USER_KEY);
      const token = safeLocalStorageGet(TOKEN_KEY);

      if (!token || !expiry || !userData) {
        return false;
      }

      const isExpired = new Date().getTime() > parseInt(expiry);
      return !isExpired;
    } catch (error) {
      return false;
    }
  };

  const [isAuthenticated, setIsAuthenticated] = useState(getInitialAuthState);

  const checkSessionState = () => {
    try {
      const expiry = safeLocalStorageGet(SESSION_KEY);
      const userData = safeLocalStorageGet(USER_KEY);
      const token = safeLocalStorageGet(TOKEN_KEY);

      if (!token || !expiry || !userData) {
        return false;
      }

      const isExpired = new Date().getTime() > parseInt(expiry);
      return !isExpired;
    } catch (error) {
      console.error('Session check error:', error);
      return false;
    }
  };

  const clearSession = useCallback(() => {
    try {
      safeLocalStorageRemove(SESSION_KEY);
      safeLocalStorageRemove(USER_KEY);
      safeLocalStorageRemove(TOKEN_KEY);
    } catch (error) {
      console.error('Session clear error:', error);
    }
  }, []);

  const extendSession = useCallback(() => {
    try {
      const newExpiry = new Date().getTime() + SESSION_TIMEOUT;
      safeLocalStorageSet(SESSION_KEY, newExpiry.toString());
    } catch (error) {
      console.error('Session extension error:', error);
    }
  }, []);

  const startSession = useCallback((userData) => {
    try {
      const newExpiry = new Date().getTime() + SESSION_TIMEOUT;
      const success1 = safeLocalStorageSet(SESSION_KEY, newExpiry.toString());
      const success2 = safeLocalStorageSet(USER_KEY, JSON.stringify(userData));

      if (success1 && success2) {
        setIsAuthenticated(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Session start error:', error);
      return false;
    }
  }, []);

  const endSession = useCallback(() => {
    clearSession();
    setIsAuthenticated(false);
    navigate('/login');
  }, [clearSession, navigate]);

  useEffect(() => {
    const checkSession = () => {
      const authenticated = checkSessionState();
      if (!authenticated && isAuthenticated) {
        setIsAuthenticated(false);
        clearSession();
        navigate('/login', { state: { from: location }, replace: true });
      }
    };

    const interval = setInterval(checkSession, CHECK_INTERVAL);

    const handleUserActivity = () => {
      if (isAuthenticated) {
        extendSession();
      }
    };

    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    activityEvents.forEach(event => {
      window.addEventListener(event, handleUserActivity, { passive: true });
    });

    return () => {
      clearInterval(interval);
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });
    };
  }, [isAuthenticated, extendSession, clearSession, navigate, location]);

  return {
    isAuthenticated,
    startSession,
    extendSession,
    endSession
  };
}
